import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import './AdminPanel.css';

const AdminPanel = ({ onClose }) => {
  const { token, isAdmin, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'logs'
  const [logContent, setLogContent] = useState('');
  const [logType, setLogType] = useState('security');
  const [logLines, setLogLines] = useState(100);
  const [logLoading, setLogLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This will also delete all their data.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete user');
      }

      // Refresh the user list
      await fetchUsers();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleToggleAdmin = async (userId, username, currentAdminStatus) => {
    const action = currentAdminStatus ? 'remove admin privileges from' : 'grant admin privileges to';
    if (!window.confirm(`Are you sure you want to ${action} user "${username}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/admin`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_admin: !currentAdminStatus })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update admin status');
      }

      // Refresh the user list
      await fetchUsers();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleToggleApproval = async (userId, username, currentApprovalStatus) => {
    const action = currentApprovalStatus ? 'revoke approval from' : 'approve';
    if (!window.confirm(`Are you sure you want to ${action} user "${username}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_approved: !currentApprovalStatus })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update approval status');
      }

      // Refresh the user list
      await fetchUsers();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const fetchLogs = async () => {
    try {
      setLogLoading(true);
      const response = await fetch(`${API_URL}/api/admin/logs/view?log_type=${logType}&lines=${logLines}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }

      const data = await response.json();
      setLogContent(data.content);
      setError(null);
    } catch (err) {
      setError(err.message);
      setLogContent('');
    } finally {
      setLogLoading(false);
    }
  };

  const downloadLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/logs/download?log_type=${logType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download logs');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${logType}.log`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(`Error downloading logs: ${err.message}`);
    }
  };

  if (!isAdmin) {
    return (
      <div className="admin-panel-overlay">
        <div className="admin-panel">
          <div className="admin-panel-header">
            <h2>Access Denied</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="admin-panel-content">
            <p>You do not have admin privileges.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={(e) => e.stopPropagation()}>
        <div className="admin-panel-header">
          <h2>Admin Panel</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="admin-panel-tabs">
          <button
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            User Management
          </button>
          <button
            className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('logs');
              if (!logContent) fetchLogs();
            }}
          >
            Security Logs
          </button>
        </div>

        <div className="admin-panel-content">
          {activeTab === 'users' && (
            <>
              {loading && <div className="loading">Loading users...</div>}
              {error && <div className="error">Error: {error}</div>}

              {!loading && !error && (
                <div className="users-table-container">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Admin</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className={u.id === user.id ? 'current-user' : ''}>
                          <td>{u.id}</td>
                          <td>
                            {u.username}
                            {u.id === user.id && <span className="badge">You</span>}
                          </td>
                          <td>{u.email}</td>
                          <td>
                            {u.is_admin ? (
                              <span className="admin-badge">Admin</span>
                            ) : (
                              <span className="user-badge">User</span>
                            )}
                          </td>
                          <td>
                            {u.is_approved ? (
                              <span className="approved-badge">Approved</span>
                            ) : (
                              <span className="pending-badge">Pending</span>
                            )}
                          </td>
                          <td>{new Date(u.created_at).toLocaleDateString()}</td>
                          <td className="actions">
                            {!u.is_approved && (
                              <button
                                className="approve-btn"
                                onClick={() => handleToggleApproval(u.id, u.username, u.is_approved)}
                                title="Approve user"
                              >
                                Approve
                              </button>
                            )}
                            {u.is_approved && (
                              <button
                                className="revoke-btn"
                                onClick={() => handleToggleApproval(u.id, u.username, u.is_approved)}
                                title="Revoke approval"
                              >
                                Revoke
                              </button>
                            )}
                            <button
                              className="admin-toggle-btn"
                              onClick={() => handleToggleAdmin(u.id, u.username, u.is_admin)}
                              disabled={u.id === user.id}
                              title={u.id === user.id ? "Cannot modify your own admin status" : "Toggle admin privileges"}
                            >
                              {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteUser(u.id, u.username)}
                              disabled={u.id === user.id}
                              title={u.id === user.id ? "Cannot delete yourself" : "Delete user"}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {activeTab === 'logs' && (
            <>
              <div className="logs-controls">
                <div className="log-options">
                  <label>
                    Log Type:
                    <select value={logType} onChange={(e) => setLogType(e.target.value)}>
                      <option value="security">Security Log</option>
                      <option value="app">Application Log</option>
                    </select>
                  </label>
                  <label>
                    Lines:
                    <input
                      type="number"
                      value={logLines}
                      onChange={(e) => setLogLines(parseInt(e.target.value) || 100)}
                      min="10"
                      max="10000"
                      step="50"
                    />
                  </label>
                  <button onClick={fetchLogs} disabled={logLoading}>
                    {logLoading ? 'Loading...' : 'Refresh Logs'}
                  </button>
                  <button onClick={downloadLogs}>
                    Download Full Log
                  </button>
                </div>
              </div>

              {error && <div className="error">Error: {error}</div>}

              {logContent && (
                <div className="logs-viewer">
                  <pre>{logContent}</pre>
                </div>
              )}

              {!logContent && !error && !logLoading && (
                <div className="no-logs">
                  No log content available. Click "Refresh Logs" to load.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
